import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import {
  Database,
  Search,
  Plus,
  FolderOpen,
  FileText,
  Users,
  Briefcase,
  Award,
  DollarSign,
  Shield,
  Building2,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  CheckCircle2,
  AlertCircle,
  Clock,
  TrendingUp,
  Upload,
  Settings,
  BarChart3,
  Filter,
  Eye,
  Download,
  Edit,
  Trash2,
  User,
  BarChart2,
} from 'lucide-react';
import { Container } from '../styles/GlobalStyles';
import { theme } from '../styles/theme';
import Particles from '../components/Particles';
import DocumentUploadModal from '../components/DocumentUploadModal';
import {
  KnowledgeBaseApi,
  CompanyProfile,
  SetupProgress,
  KnowledgeBaseStats,
  CategoryInfo,
  CompanyDocument,
  CATEGORY_GROUPS,
  CATEGORY_DISPLAY_NAMES,
  CATEGORY_ICONS,
  CompanyDocumentCategory,
} from '../services/knowledgeBaseApi';

// =============================================================================
// MOTION
// =============================================================================

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const viewSwap = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// =============================================================================
// PAGE SHELL — coherent with /profile, /settings, /contact
// =============================================================================

const KbContainer = styled.div`
  min-height: calc(100vh - 72px);
  background: ${theme.colors.backgroundSecondary};
  padding: ${theme.spacing[8]} 0 ${theme.spacing[16]};
  margin: 30px 30px 0;
  border: 1px solid ${theme.colors.border};
  border-radius: 20px;
  position: relative;
  /* clip (not hidden) so the sticky toolbar inside the categories section can
     attach to the viewport top instead of being trapped here. */
  overflow: clip;

  @media (max-width: ${theme.breakpoints.md}) {
    margin: 20px;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    margin: 0;
    border-radius: 0;
    border: none;
  }
`;

const ContentWrapper = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${theme.spacing[4]};
  animation: ${fadeUp} 0.45s ease-out both;
`;

// =============================================================================
// PAGE HEADER
// =============================================================================

const PageHeader = styled.header`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${theme.spacing[6]};
  margin-bottom: ${theme.spacing[8]};
  padding-bottom: ${theme.spacing[6]};
  border-bottom: 1px solid ${theme.colors.borderLight};

  @media (max-width: ${theme.breakpoints.md}) {
    flex-direction: column;
    align-items: stretch;
    gap: ${theme.spacing[4]};
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  min-width: 0;
`;

const Crumb = styled.div`
  font-size: 11px;
  font-weight: ${theme.typography.fontWeight.semibold};
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${theme.colors.textMuted};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${theme.colors.primary[500]};
  }
`;

const Title = styled.h1`
  font-family: ${theme.typography.fontFamily.display};
  font-size: ${theme.typography.fontSize['3xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.textPrimary};
  margin: 0;
  letter-spacing: -0.02em;
  line-height: 1.15;
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.textSecondary};
  margin: ${theme.spacing[1]} 0 0;
  max-width: 560px;
`;

const HeaderRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${theme.spacing[3]};

  @media (max-width: ${theme.breakpoints.md}) {
    align-items: stretch;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: flex-end;
  }
`;

// Adaptive status pill — colours respond to KB completeness:
// success when complete, warning when missing critical, primary otherwise.
type StatusTone = 'success' | 'warning' | 'primary';

const StatusPill = styled.div<{ $tone: StatusTone }>`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: 8px ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.full};
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.borderLight};
  box-shadow: ${theme.shadows.sm};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textSecondary};
  white-space: nowrap;

  & strong {
    color: ${theme.colors.textPrimary};
    font-weight: ${theme.typography.fontWeight.semibold};
  }

  & .pulse {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
    ${({ $tone }) =>
      $tone === 'success'
        ? css`
            background: ${theme.colors.success[500]};
            box-shadow: 0 0 0 4px ${theme.colors.success[500]}24;
          `
        : $tone === 'warning'
        ? css`
            background: ${theme.colors.warning[500]};
            box-shadow: 0 0 0 4px ${theme.colors.warning[500]}24;
          `
        : css`
            background: ${theme.colors.primary[500]};
            box-shadow: 0 0 0 4px ${theme.colors.primary[500]}24;
          `}
  }
`;

const ActionsRow = styled.div`
  display: flex;
  gap: ${theme.spacing[2]};
  flex-wrap: wrap;
`;

const PrimaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[5]};
  background: ${theme.colors.primary[500]};
  color: #fff;
  border: 1px solid ${theme.colors.primary[600]};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-family: inherit;
  cursor: pointer;
  transition: transform ${theme.transitions.base}, box-shadow ${theme.transitions.base},
    background ${theme.transitions.base};
  box-shadow: 0 6px 14px -6px rgba(0, 0, 41, 0.45);

  &:hover:not(:disabled) {
    background: ${theme.colors.primary[700]};
    transform: translateY(-1px);
    box-shadow: 0 10px 18px -6px rgba(0, 0, 41, 0.55);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }
`;

const SecondaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[5]};
  background: transparent;
  color: ${theme.colors.textPrimary};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  font-family: inherit;
  cursor: pointer;
  transition: all ${theme.transitions.base};

  &:hover:not(:disabled) {
    background: ${theme.colors.backgroundSecondary};
    border-color: ${theme.colors.primary[500]};
    color: ${theme.colors.primary[700]};
  }
`;

// =============================================================================
// SETUP PROGRESS PANEL
// =============================================================================

const ProgressPanel = styled.section`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.borderLight};
  border-radius: ${theme.borderRadius.xl};
  box-shadow: ${theme.shadows.sm};
  padding: ${theme.spacing[6]} ${theme.spacing[6]} ${theme.spacing[6]};
  margin-bottom: ${theme.spacing[6]};
  position: relative;
  overflow: hidden;

  /* Subtle navy stripe at the top to anchor visually. */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(
      90deg,
      ${theme.colors.primary[500]} 0%,
      ${theme.colors.primary[700]} 100%
    );
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing[5]};
  }
`;

const ProgressTopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[5]};

  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Eyebrow = styled.div`
  font-size: 11px;
  font-weight: ${theme.typography.fontWeight.semibold};
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${theme.colors.primary[500]};
  margin-bottom: ${theme.spacing[2]};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  & .num {
    color: ${theme.colors.textMuted};
    font-variant-numeric: tabular-nums;
  }
`;

const SubHeading = styled.h2`
  font-family: ${theme.typography.fontFamily.display};
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  margin: 0 0 ${theme.spacing[1]};
  letter-spacing: -0.015em;
`;

const SubHelper = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textMuted};
  line-height: ${theme.typography.lineHeight.relaxed};
  margin: 0;
  max-width: 540px;
`;

const ProgressRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: ${theme.spacing[2]};
  flex-shrink: 0;

  @media (max-width: ${theme.breakpoints.sm}) {
    align-items: flex-start;
  }
`;

// Tone-down: 0% reads as informational grey, 1–49% soft amber, 50–79% accent, ≥80% green
const progressColor = (p: number, shade: 'main' | 'dark' = 'main') => {
  if (p >= 80) return shade === 'dark' ? theme.colors.success[600] : theme.colors.success[500];
  if (p >= 50) return shade === 'dark' ? theme.colors.primary[700] : theme.colors.primary[500];
  if (p >= 1) return shade === 'dark' ? theme.colors.warning[600] : theme.colors.warning[500];
  return shade === 'dark' ? theme.colors.textMuted : theme.colors.textMuted;
};

const BigPercent = styled.div<{ $pct: number }>`
  font-family: ${theme.typography.fontFamily.display};
  font-size: 40px;
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${({ $pct }) => progressColor($pct)};
  line-height: 1;
  letter-spacing: -0.03em;
  font-variant-numeric: tabular-nums;
`;

const InlineChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing[2]};
  margin-top: ${theme.spacing[1]};
`;

const InlineChip = styled.div<{ $tone?: 'success' | 'warning' | 'muted' }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.full};
  font-size: 11px;
  font-weight: ${theme.typography.fontWeight.medium};
  font-variant-numeric: tabular-nums;

  ${({ $tone }) =>
    $tone === 'success'
      ? css`
          background: ${theme.colors.success[100]};
          color: ${theme.colors.success[700]};
        `
      : $tone === 'warning'
      ? css`
          background: ${theme.colors.warning[100]};
          color: ${theme.colors.warning[700]};
        `
      : css`
          background: ${theme.colors.backgroundTertiary};
          color: ${theme.colors.textSecondary};
        `}

  & strong {
    font-weight: ${theme.typography.fontWeight.semibold};
  }
`;

const ProgressTrack = styled.div`
  position: relative;
  width: 100%;
  height: 14px;
  background: ${theme.colors.backgroundTertiary};
  border-radius: ${theme.borderRadius.full};
  overflow: hidden;
  box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.06);
`;

const ProgressFill = styled.div<{ $pct: number }>`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  background: linear-gradient(
    90deg,
    ${({ $pct }) => progressColor($pct)},
    ${({ $pct }) => progressColor($pct, 'dark')}
  );
  border-radius: ${theme.borderRadius.full};
  transition: width 0.6s cubic-bezier(0.16, 1, 0.3, 1), background 0.4s ease;
`;

const TickWrap = styled.div`
  position: relative;
  margin-top: ${theme.spacing[1]};
  height: 18px;
`;

const Tick = styled.div`
  position: absolute;
  top: -8px;
  left: 50%;
  width: 2px;
  height: 12px;
  background: ${theme.colors.textMuted};
  opacity: 0.5;
  border-radius: 1px;
`;

const TickLabel = styled.span`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  font-size: 10px;
  color: ${theme.colors.textMuted};
  letter-spacing: 0.06em;
  text-transform: uppercase;
  white-space: nowrap;
`;

// =============================================================================
// WARNING BANNER (InlineNote pattern, warning-tone)
// =============================================================================

const WarningBanner = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: ${theme.colors.warning[100]};
  border: 1px solid ${theme.colors.warning[500]}33;
  border-left: 3px solid ${theme.colors.warning[500]};
  border-radius: ${theme.borderRadius.md};
  margin-bottom: ${theme.spacing[6]};

  & .icon {
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    border-radius: ${theme.borderRadius.sm};
    background: ${theme.colors.surface};
    color: ${theme.colors.warning[700]};
    border: 1px solid ${theme.colors.warning[500]}40;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  & .copy {
    flex: 1;
    min-width: 0;
    font-size: ${theme.typography.fontSize.sm};
    color: ${theme.colors.warning[700]};
    line-height: 1.5;
  }

  & strong {
    color: ${theme.colors.warning[700]};
    font-weight: ${theme.typography.fontWeight.semibold};
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    flex-wrap: wrap;
  }
`;

const WarningAction = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  background: ${theme.colors.warning[600]};
  color: #fff;
  border: 1px solid ${theme.colors.warning[700]};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  transition: background ${theme.transitions.base}, transform ${theme.transitions.base};
  box-shadow: 0 4px 10px -4px rgba(202, 138, 4, 0.4);

  &:hover {
    background: ${theme.colors.warning[700]};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

// =============================================================================
// STATS GRID
// =============================================================================

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[8]};

  @media (max-width: ${theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.borderLight};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
  transition: transform ${theme.transitions.base}, box-shadow ${theme.transitions.base},
    border-color ${theme.transitions.base};

  &:hover {
    transform: translateY(-2px);
    border-color: ${theme.colors.primary[200]};
    box-shadow: 0 8px 18px -10px rgba(0, 0, 41, 0.18);
  }
`;

type StatTone = 'primary' | 'success' | 'warning' | 'accent';

const StatIconTile = styled.span<{ $tone: StatTone }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: ${theme.borderRadius.md};
  border: 1px solid;

  ${({ $tone }) =>
    $tone === 'success'
      ? css`
          background: ${theme.colors.success[100]};
          color: ${theme.colors.success[600]};
          border-color: ${theme.colors.success[500]}40;
        `
      : $tone === 'warning'
      ? css`
          background: ${theme.colors.warning[100]};
          color: ${theme.colors.warning[600]};
          border-color: ${theme.colors.warning[500]}40;
        `
      : $tone === 'accent'
      ? css`
          background: ${theme.colors.primary[50]};
          color: ${theme.colors.primary[700]};
          border-color: ${theme.colors.primary[200]};
        `
      : css`
          background: ${theme.colors.primary[50]};
          color: ${theme.colors.primary[500]};
          border-color: ${theme.colors.primary[100]};
        `}
`;

const StatBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StatValue = styled.div`
  font-family: ${theme.typography.fontFamily.display};
  font-size: 30px;
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  letter-spacing: -0.025em;
  line-height: 1.05;
  font-variant-numeric: tabular-nums;
`;

const StatLabel = styled.div`
  font-size: 11px;
  font-weight: ${theme.typography.fontWeight.semibold};
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${theme.colors.textMuted};
  margin-top: ${theme.spacing[1]};
`;

// Optional micro-bar under the value — subtle visual texture for "Most Used"
const MicroBar = styled.div<{ $pct: number }>`
  margin-top: ${theme.spacing[2]};
  height: 3px;
  background: ${theme.colors.borderLight};
  border-radius: ${theme.borderRadius.full};
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    width: ${({ $pct }) => Math.max(8, Math.min(100, $pct))}%;
    background: ${theme.colors.primary[500]};
    border-radius: ${theme.borderRadius.full};
    transition: width 0.6s cubic-bezier(0.16, 1, 0.3, 1);
  }
`;

// =============================================================================
// FIRST-RUN NAVY HERO
// =============================================================================

const FirstRunHero = styled.div`
  position: relative;
  padding: ${theme.spacing[10]} ${theme.spacing[8]};
  border-radius: ${theme.borderRadius.xl};
  background:
    radial-gradient(120% 80% at 100% 0%, rgba(255, 255, 255, 0.10) 0%, transparent 60%),
    linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.primary[700]} 100%);
  color: rgba(255, 255, 255, 0.95);
  overflow: hidden;
  isolation: isolate;
  border: 1px solid ${theme.colors.primary[700]};
  box-shadow: ${theme.shadows.md};
  margin-bottom: ${theme.spacing[8]};
  text-align: left;

  /* Grain texture for tactile depth on the deep navy. */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.045 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>");
    opacity: 0.7;
    pointer-events: none;
    mix-blend-mode: screen;
    z-index: -1;
  }

  /* Soft corner glow. */
  &::after {
    content: '';
    position: absolute;
    top: -120px;
    right: -120px;
    width: 360px;
    height: 360px;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.18) 0%, transparent 65%);
    pointer-events: none;
  }

  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing[8]} ${theme.spacing[5]};
  }
`;

const FirstRunEyebrow = styled.div`
  font-size: 11px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  font-weight: ${theme.typography.fontWeight.semibold};
  color: rgba(255, 255, 255, 0.65);
  margin-bottom: ${theme.spacing[3]};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  & .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${theme.colors.success[500]};
    box-shadow: 0 0 0 3px ${theme.colors.success[500]}33;
  }
`;

const FirstRunTitle = styled.h2`
  font-family: ${theme.typography.fontFamily.display};
  font-size: 32px;
  font-weight: ${theme.typography.fontWeight.bold};
  color: #fff;
  margin: 0 0 ${theme.spacing[2]};
  letter-spacing: -0.025em;
  line-height: 1.15;
  max-width: 640px;

  @media (max-width: ${theme.breakpoints.sm}) {
    font-size: 26px;
  }
`;

const FirstRunSub = styled.p`
  color: rgba(255, 255, 255, 0.75);
  font-size: ${theme.typography.fontSize.base};
  margin: 0 0 ${theme.spacing[6]};
  max-width: 560px;
  line-height: ${theme.typography.lineHeight.relaxed};
`;

const FirstRunChipRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[6]};
  max-width: 760px;

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const FirstRunChip = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[4]};
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: ${theme.borderRadius.lg};
  color: #fff;
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-family: inherit;
  cursor: pointer;
  text-align: left;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  transition: background ${theme.transitions.base}, transform ${theme.transitions.base},
    border-color ${theme.transitions.base};

  & .icon {
    width: 38px;
    height: 38px;
    border-radius: ${theme.borderRadius.md};
    background: rgba(255, 255, 255, 0.15);
    border: 1px solid rgba(255, 255, 255, 0.22);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  & .body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  & .label {
    color: #fff;
    font-weight: ${theme.typography.fontWeight.semibold};
    letter-spacing: -0.005em;
  }

  & .meta {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.7);
    font-weight: ${theme.typography.fontWeight.normal};
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.16);
    border-color: rgba(255, 255, 255, 0.28);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const FirstRunFooter = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[6]};
  flex-wrap: wrap;
  padding-top: ${theme.spacing[5]};
  border-top: 1px solid rgba(255, 255, 255, 0.15);
`;

const FirstRunLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  background: none;
  border: none;
  padding: 0;
  color: #fff;
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-family: inherit;
  cursor: pointer;
  text-decoration: none;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: -2px;
    width: 100%;
    height: 1px;
    background: rgba(255, 255, 255, 0.4);
    transition: background ${theme.transitions.base};
  }

  &:hover {
    color: #fff;
  }

  &:hover::after {
    background: rgba(255, 255, 255, 0.85);
  }
`;

// =============================================================================
// CATEGORIES SECTION
// =============================================================================

const CategoriesSection = styled.section`
  margin-bottom: ${theme.spacing[8]};
  animation: ${viewSwap} 0.3s ease-out both;
`;

const SectionHead = styled.div`
  margin-bottom: ${theme.spacing[5]};
`;

// Sticky toolbar holding search + chips. Uses backdrop-blur so the categories
// underneath show through faintly while it's pinned.
const StickyToolbar = styled.div`
  position: sticky;
  top: 0;
  z-index: 8;
  margin: 0 -${theme.spacing[4]} ${theme.spacing[4]};
  padding: ${theme.spacing[4]};
  background: rgba(248, 250, 251, 0.85);
  backdrop-filter: blur(14px) saturate(140%);
  -webkit-backdrop-filter: blur(14px) saturate(140%);
  border-bottom: 1px solid ${theme.colors.borderLight};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const SearchBlock = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  flex-wrap: wrap;
`;

const SearchInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 240px;
  max-width: 480px;
`;

const SearchIconLeft = styled.span`
  position: absolute;
  left: ${theme.spacing[3]};
  display: inline-flex;
  align-items: center;
  color: ${theme.colors.textMuted};
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${theme.spacing[3]} ${theme.spacing[4]} ${theme.spacing[3]} ${theme.spacing[10]};
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.textPrimary};
  font-size: ${theme.typography.fontSize.sm};
  font-family: inherit;
  transition: all ${theme.transitions.base};

  &:hover:not(:focus) {
    border-color: ${theme.colors.borderHover};
    background: ${theme.colors.surfaceHover};
  }

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    background: ${theme.colors.surface};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}1a, 0 1px 2px rgba(0, 0, 0, 0.04);
  }

  &::placeholder {
    color: ${theme.colors.textMuted};
  }
`;

const SearchMatchInfo = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.textMuted};
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  font-variant-numeric: tabular-nums;

  & code {
    font-family: ${theme.typography.fontFamily.mono};
    color: ${theme.colors.textPrimary};
    background: ${theme.colors.backgroundTertiary};
    padding: 2px 6px;
    border-radius: ${theme.borderRadius.sm};
    font-size: 11px;
  }
`;

const FilterChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing[2]};
`;

type ChipTone = 'primary' | 'warning' | 'error';

const FilterChip = styled.button<{ $active: boolean; $tone: ChipTone }>`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: 7px ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-family: inherit;
  cursor: pointer;
  transition: all ${theme.transitions.base};

  ${({ $active, $tone }) => {
    const palette = {
      primary: {
        bg: theme.colors.primary[500],
        border: theme.colors.primary[600],
        bgHover: theme.colors.primary[700],
        countBg: 'rgba(255, 255, 255, 0.2)',
      },
      warning: {
        bg: theme.colors.warning[500],
        border: theme.colors.warning[600],
        bgHover: theme.colors.warning[600],
        countBg: 'rgba(255, 255, 255, 0.25)',
      },
      error: {
        bg: theme.colors.error[500],
        border: theme.colors.error[600],
        bgHover: theme.colors.error[600],
        countBg: 'rgba(255, 255, 255, 0.25)',
      },
    } as const;
    const t = palette[$tone];
    return $active
      ? css`
          background: ${t.bg};
          color: #fff;
          border: 1px solid ${t.border};
          box-shadow: 0 4px 10px -4px rgba(0, 0, 41, 0.35);

          &:hover {
            background: ${t.bgHover};
            transform: translateY(-1px);
          }
        `
      : css`
          background: ${theme.colors.surface};
          color: ${theme.colors.textSecondary};
          border: 1px solid ${theme.colors.borderLight};

          &:hover {
            background: ${theme.colors.surfaceHover};
            border-color: ${theme.colors.borderHover};
            color: ${theme.colors.textPrimary};
          }
        `;
  }}
`;

const ChipCount = styled.span<{ $active: boolean }>`
  font-size: 10px;
  font-variant-numeric: tabular-nums;
  font-weight: ${theme.typography.fontWeight.bold};
  padding: 1px 6px;
  border-radius: ${theme.borderRadius.full};
  ${({ $active }) =>
    $active
      ? css`
          background: rgba(255, 255, 255, 0.22);
          color: #fff;
        `
      : css`
          background: ${theme.colors.backgroundTertiary};
          color: ${theme.colors.textMuted};
        `}
`;

const JumpList = styled.nav`
  display: flex;
  gap: ${theme.spacing[2]};
  overflow-x: auto;
  padding: ${theme.spacing[1]} 0 ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[3]};
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${theme.colors.borderLight};
    border-radius: 3px;
  }
`;

const JumpChip = styled.button<{ $active: boolean; $disabled?: boolean }>`
  flex-shrink: 0;
  padding: 5px ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.full};
  font-size: 12px;
  font-weight: ${theme.typography.fontWeight.medium};
  font-family: inherit;
  white-space: nowrap;
  transition: all ${theme.transitions.base};

  ${({ $active, $disabled }) =>
    $disabled
      ? css`
          background: transparent;
          color: ${theme.colors.textMuted};
          border: 1px dashed ${theme.colors.borderLight};
          cursor: not-allowed;
          opacity: 0.55;
        `
      : $active
      ? css`
          background: ${theme.colors.primary[500]};
          color: #fff;
          border: 1px solid ${theme.colors.primary[600]};
          cursor: pointer;
          box-shadow: 0 4px 10px -4px rgba(0, 0, 41, 0.35);
        `
      : css`
          background: ${theme.colors.surface};
          color: ${theme.colors.textSecondary};
          border: 1px solid ${theme.colors.borderLight};
          cursor: pointer;

          &:hover {
            background: ${theme.colors.surfaceHover};
            border-color: ${theme.colors.borderHover};
            color: ${theme.colors.textPrimary};
          }
        `}
`;

const SearchEmptyRow = styled.div`
  padding: ${theme.spacing[8]} ${theme.spacing[6]};
  text-align: center;
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSize.sm};
  background: ${theme.colors.surface};
  border: 1px dashed ${theme.colors.borderLight};
  border-radius: ${theme.borderRadius.lg};

  & code {
    font-family: ${theme.typography.fontFamily.mono};
    color: ${theme.colors.textPrimary};
    background: ${theme.colors.backgroundTertiary};
    padding: 2px 6px;
    border-radius: ${theme.borderRadius.sm};
  }
`;

// =============================================================================
// CATEGORY GROUP & CARDS
// =============================================================================

const CategoryGroup = styled.div`
  margin-bottom: ${theme.spacing[4]};
  border: 1px solid ${theme.colors.borderLight};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.surface};
  overflow: hidden;
  box-shadow: ${theme.shadows.sm};
`;

const CategoryGroupHeader = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  width: 100%;
  padding: ${theme.spacing[4]} ${theme.spacing[5]};
  background: ${theme.colors.surface};
  border: none;
  font-family: inherit;
  cursor: pointer;
  text-align: left;
  transition: background ${theme.transitions.base};

  &:hover {
    background: ${theme.colors.backgroundSecondary};
  }
`;

const GroupChevron = styled.span<{ $expanded: boolean }>`
  display: inline-flex;
  align-items: center;
  color: ${theme.colors.textMuted};
  transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  transform: rotate(${({ $expanded }) => ($expanded ? '0deg' : '-90deg')});
  flex-shrink: 0;
`;

const GroupIconTile = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: ${theme.borderRadius.md};
  background: ${theme.colors.primary[50]};
  color: ${theme.colors.primary[500]};
  border: 1px solid ${theme.colors.primary[100]};
  font-size: 18px;
  flex-shrink: 0;
`;

const GroupName = styled.h3`
  font-family: ${theme.typography.fontFamily.display};
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  flex: 1;
  margin: 0;
  letter-spacing: -0.012em;
`;

const GroupCountPill = styled.span<{ $allFilled: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-family: ${theme.typography.fontFamily.mono};
  font-size: 12px;
  font-weight: ${theme.typography.fontWeight.semibold};
  font-variant-numeric: tabular-nums;
  padding: 4px ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.full};
  border: 1px solid;
  ${({ $allFilled }) =>
    $allFilled
      ? css`
          background: ${theme.colors.success[100]};
          color: ${theme.colors.success[700]};
          border-color: ${theme.colors.success[500]}40;
        `
      : css`
          background: ${theme.colors.backgroundTertiary};
          color: ${theme.colors.textSecondary};
          border-color: ${theme.colors.borderLight};
        `}
`;

const CategoryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[4]} ${theme.spacing[5]} ${theme.spacing[5]};
  border-top: 1px solid ${theme.colors.borderLight};
  background: ${theme.colors.backgroundSecondary};

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
    padding: ${theme.spacing[3]} ${theme.spacing[4]} ${theme.spacing[4]};
  }
`;

const CategoryCard = styled.div<{ $hasDocuments: boolean; $isCritical: boolean }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  min-height: 64px;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: ${theme.colors.surface};
  border: 1px solid
    ${({ $isCritical }) =>
      $isCritical ? `${theme.colors.error[500]}55` : theme.colors.borderLight};
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  transition: transform 0.15s ease-out, border-color 0.15s ease-out, box-shadow 0.15s ease-out,
    background 0.15s ease-out;
  position: relative;

  &:hover,
  &:focus-visible {
    transform: translateY(-2px);
    border-color: ${theme.colors.primary[300]};
    box-shadow: 0 8px 18px -10px rgba(0, 0, 41, 0.18);
    background: ${theme.colors.surface};
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}33,
      0 8px 18px -10px rgba(0, 0, 41, 0.18);
  }

  &:hover svg.kb-tile-chevron,
  &:focus-visible svg.kb-tile-chevron {
    transform: translateX(3px);
    color: ${theme.colors.primary[500]};
  }

  &:active {
    transform: translateY(0);
  }

  @media (prefers-reduced-motion: reduce) {
    transition: border-color 0.15s ease-out, box-shadow 0.15s ease-out;
    &:hover,
    &:focus-visible {
      transform: none;
    }
    &:hover svg.kb-tile-chevron,
    &:focus-visible svg.kb-tile-chevron {
      transform: none;
    }
  }
`;

const StatusIconTile = styled.span<{ $filled: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: ${theme.borderRadius.md};
  flex-shrink: 0;
  border: 1px solid;

  ${({ $filled }) =>
    $filled
      ? css`
          background: ${theme.colors.primary[50]};
          color: ${theme.colors.primary[600]};
          border-color: ${theme.colors.primary[100]};
        `
      : css`
          background: ${theme.colors.backgroundTertiary};
          color: ${theme.colors.textMuted};
          border-color: ${theme.colors.borderLight};
        `}
`;

const CategoryBody = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CategoryName = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  font-size: ${theme.typography.fontSize.sm};
  letter-spacing: -0.005em;
`;

const CategoryNameText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CategoryDescription = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.textMuted};
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

// Match ProfilePage CriticalTag spec exactly: 10px uppercase, error[100]/80
// background, error[600] text, 1px 5px padding, 3px border-radius, medium weight.
const CriticalTag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 1px 5px;
  margin-left: ${theme.spacing[1]};
  background: ${theme.colors.error[100]}80;
  color: ${theme.colors.error[600]};
  border-radius: 3px;
  font-size: 10px;
  font-weight: ${theme.typography.fontWeight.medium};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  line-height: 1.4;
  white-space: nowrap;
  flex-shrink: 0;
`;

const CountPill = styled.div<{ $hasDocuments: boolean }>`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.full};
  font-family: ${theme.typography.fontFamily.mono};
  font-size: 11px;
  font-weight: ${theme.typography.fontWeight.semibold};
  font-variant-numeric: tabular-nums;
  border: 1px solid;
  ${({ $hasDocuments }) =>
    $hasDocuments
      ? css`
          background: ${theme.colors.success[100]};
          color: ${theme.colors.success[700]};
          border-color: ${theme.colors.success[500]}40;
        `
      : css`
          background: ${theme.colors.backgroundTertiary};
          color: ${theme.colors.textMuted};
          border-color: ${theme.colors.borderLight};
        `}
`;

const CategoryChevron = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  color: ${theme.colors.textMuted};

  svg {
    transition: transform 150ms ease-out, color 150ms ease-out;
  }
`;

// =============================================================================
// DOCUMENTS VIEW (per-category)
// =============================================================================

const DocumentsViewContainer = styled.div`
  margin-bottom: ${theme.spacing[8]};
  animation: ${viewSwap} 0.3s ease-out both;
`;

const DocumentsHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[6]};
  padding-bottom: ${theme.spacing[5]};
  border-bottom: 1px solid ${theme.colors.borderLight};

  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const DocsBreadcrumb = styled.nav`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  min-width: 0;
`;

const DocsBackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  padding: 0;
  background: none;
  border: none;
  color: ${theme.colors.textMuted};
  font-size: 11px;
  font-weight: ${theme.typography.fontWeight.semibold};
  letter-spacing: 0.18em;
  text-transform: uppercase;
  font-family: inherit;
  cursor: pointer;
  transition: color ${theme.transitions.base};
  width: max-content;

  &:hover {
    color: ${theme.colors.primary[500]};
  }

  & svg {
    transition: transform ${theme.transitions.base};
  }

  &:hover svg {
    transform: translateX(-2px);
  }
`;

const DocsHeading = styled.h2`
  font-family: ${theme.typography.fontFamily.display};
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  margin: 0;
  letter-spacing: -0.02em;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
`;

const DocsHeadingPath = styled.span`
  color: ${theme.colors.textMuted};
  font-weight: ${theme.typography.fontWeight.normal};
  font-size: ${theme.typography.fontSize.lg};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  & svg {
    color: ${theme.colors.textMuted};
  }
`;

const DocumentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const DocumentCard = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.borderLight};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[6]};
  transition: transform ${theme.transitions.base}, box-shadow ${theme.transitions.base},
    border-color ${theme.transitions.base};

  &:hover {
    border-color: ${theme.colors.primary[200]};
    box-shadow: 0 8px 18px -10px rgba(0, 0, 41, 0.18);
  }
`;

const DocumentCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[3]};

  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
  }
`;

const DocumentTitle = styled.h3`
  font-family: ${theme.typography.fontFamily.display};
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  margin: 0 0 ${theme.spacing[3]};
  letter-spacing: -0.012em;
  line-height: 1.3;
`;

const DocumentMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing[2]} ${theme.spacing[3]};
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.textMuted};
`;

const MetaPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px ${theme.spacing[2]};
  background: ${theme.colors.backgroundTertiary};
  border: 1px solid ${theme.colors.borderLight};
  border-radius: ${theme.borderRadius.full};
  font-size: 11px;
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.textSecondary};

  & .icon {
    color: ${theme.colors.textMuted};
    display: inline-flex;
    align-items: center;
  }
`;

const DocumentSummary = styled.p`
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.fontSize.sm};
  line-height: ${theme.typography.lineHeight.relaxed};
  margin: 0 0 ${theme.spacing[3]};
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const DocumentKeyPoints = styled.ul`
  list-style: none;
  padding: 0;
  margin: ${theme.spacing[3]} 0;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const KeyPoint = styled.li`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[2]};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textSecondary};
  line-height: 1.5;

  &::before {
    content: '';
    flex-shrink: 0;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: ${theme.colors.primary[500]};
    margin-top: 9px;
  }
`;

const DocumentActions = styled.div`
  display: flex;
  gap: ${theme.spacing[1]};
  flex-shrink: 0;

  @media (max-width: ${theme.breakpoints.sm}) {
    align-self: flex-end;
  }
`;

const ActionIconButton = styled.button<{ $tone?: 'default' | 'danger' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: ${theme.borderRadius.md};
  background: transparent;
  color: ${theme.colors.textMuted};
  border: 1px solid ${theme.colors.borderLight};
  font-family: inherit;
  cursor: pointer;
  transition: all ${theme.transitions.base};

  ${({ $tone }) =>
    $tone === 'danger'
      ? css`
          &:hover {
            background: ${theme.colors.error[500]};
            color: #fff;
            border-color: ${theme.colors.error[500]};
          }
        `
      : css`
          &:hover {
            background: ${theme.colors.primary[50]};
            color: ${theme.colors.primary[700]};
            border-color: ${theme.colors.primary[200]};
          }
        `}
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing[2]};
  margin-top: ${theme.spacing[4]};
  padding-top: ${theme.spacing[3]};
  border-top: 1px dashed ${theme.colors.borderLight};
`;

const Tag = styled.span`
  padding: 3px ${theme.spacing[2]};
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.borderLight};
  color: ${theme.colors.textMuted};
  border-radius: ${theme.borderRadius.full};
  font-size: 11px;
  font-weight: ${theme.typography.fontWeight.medium};
  letter-spacing: 0.02em;
`;

// =============================================================================
// LOADING / ERROR / EMPTY
// =============================================================================

const LoadingShell = styled.div`
  padding: ${theme.spacing[16]} ${theme.spacing[6]};
  text-align: center;
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.fontSize.sm};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing[4]};
`;

const LoadingSpinnerEl = styled.div`
  width: 28px;
  height: 28px;
  border: 3px solid ${theme.colors.borderLight};
  border-top-color: ${theme.colors.primary[500]};
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const ErrorBand = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[5]};
  background: ${theme.colors.error[100]};
  border: 1px solid ${theme.colors.error[500]}33;
  border-left: 3px solid ${theme.colors.error[500]};
  border-radius: ${theme.borderRadius.md};
  margin-bottom: ${theme.spacing[6]};

  & .icon {
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    border-radius: ${theme.borderRadius.md};
    background: ${theme.colors.surface};
    color: ${theme.colors.error[600]};
    border: 1px solid ${theme.colors.error[500]}40;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  & .body {
    flex: 1;
    min-width: 0;
  }

  & h4 {
    font-family: ${theme.typography.fontFamily.display};
    font-size: ${theme.typography.fontSize.base};
    font-weight: ${theme.typography.fontWeight.semibold};
    color: ${theme.colors.error[700]};
    margin: 0 0 ${theme.spacing[1]};
    letter-spacing: -0.01em;
  }

  & p {
    font-size: ${theme.typography.fontSize.sm};
    color: ${theme.colors.error[700]};
    line-height: ${theme.typography.lineHeight.relaxed};
    margin: 0 0 ${theme.spacing[3]};
  }
`;

const RetryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  background: ${theme.colors.error[600]};
  color: #fff;
  border: 1px solid ${theme.colors.error[700]};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  font-family: inherit;
  cursor: pointer;
  transition: background ${theme.transitions.base};

  &:hover {
    background: ${theme.colors.error[700]};
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing[16]} ${theme.spacing[6]};
  background: ${theme.colors.surface};
  border: 1px dashed ${theme.colors.borderLight};
  border-radius: ${theme.borderRadius.xl};
`;

const EmptyIcon = styled.div`
  width: 72px;
  height: 72px;
  border-radius: ${theme.borderRadius.full};
  background: ${theme.colors.backgroundTertiary};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto ${theme.spacing[4]};
  color: ${theme.colors.textMuted};
`;

const EmptyTitle = styled.h3`
  font-family: ${theme.typography.fontFamily.display};
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  margin: 0 0 ${theme.spacing[2]};
  letter-spacing: -0.015em;
`;

const EmptyDescription = styled.p`
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.fontSize.sm};
  line-height: ${theme.typography.lineHeight.relaxed};
  margin: 0 auto ${theme.spacing[6]};
  max-width: 460px;
`;

// =============================================================================
// HELPERS / TYPE ALIASES (preserved verbatim — see brief)
// =============================================================================

// Critical categories are now driven by the backend's `critical` flag on each
// CategoryInfo (CRITICAL_CATEGORIES in knowledge_base_routes.py). This keeps
// the "X critical docs missing" banner copy and the per-tile "Critical" pill
// in agreement with one another instead of maintaining a divergent FE list.
const isCategoryCritical = (c: CategoryInfo): boolean => !!c.critical;

// Filter chip identifiers
type FilterChipId = 'all' | 'critical' | 'filled' | 'empty' | 'expiring';

// Short jump-list labels for each CATEGORY_GROUPS key
const GROUP_SHORT_LABELS: Record<string, string> = {
  'Performance & Track Record': 'Performance',
  'Capabilities & Services': 'Capabilities',
  'Risk & Innovation': 'Risk',
  'Social Value & Capacity': 'Social Value',
  'Company Profile': 'Profile',
  'Team & Personnel': 'Team',
  'Experience & Case Studies': 'Experience',
  'Technical Capabilities': 'Tech',
  'Certifications & Accreditations': 'Certs',
  'Financial Documents': 'Financial',
  'Policies & Procedures': 'Policies',
  'Other': 'Other',
};

// Hand-rolled debounce for search input
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

// =============================================================================
// COMPONENT
// =============================================================================

const KnowledgeBasePage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<CompanyProfile | null>(null);
  const [setupProgress, setSetupProgress] = useState<SetupProgress | null>(null);
  const [stats, setStats] = useState<KnowledgeBaseStats | null>(null);
  const [categories, setCategories] = useState<CategoryInfo[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(undefined);
  const [viewMode, setViewMode] = useState<'overview' | 'documents'>('overview');
  const [selectedCategoryForView, setSelectedCategoryForView] = useState<string | null>(null);
  const [documents, setDocuments] = useState<CompanyDocument[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});
  const [forceFullView, setForceFullView] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearch = useDebouncedValue(searchQuery, 200);
  const [activeFilter, setActiveFilter] = useState<FilterChipId>('all');
  const [activeJumpGroup, setActiveJumpGroup] = useState<string | null>(null);
  const groupRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const toggleGroup = (groupName: string) => {
    setExpandedGroups(prev => ({ ...prev, [groupName]: !prev[groupName] }));
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [profileRes, progressRes, statsRes, categoriesRes] = await Promise.all([
        KnowledgeBaseApi.getProfile(),
        KnowledgeBaseApi.getSetupProgress(),
        KnowledgeBaseApi.getStats(),
        KnowledgeBaseApi.getCategories(),
      ]);

      if (profileRes.success && profileRes.data) {
        setProfile(profileRes.data);
      }
      if (progressRes.success && progressRes.data) {
        setSetupProgress(progressRes.data);
      }
      if (statsRes.success && statsRes.data) {
        setStats(statsRes.data);
      }
      if (categoriesRes.success && categoriesRes.data) {
        setCategories(categoriesRes.data);
      }
    } catch (err) {
      setError('Failed to load knowledge base data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchDocumentsForCategory = async (category: string) => {
    try {
      setLoadingDocuments(true);
      const response = await KnowledgeBaseApi.listDocuments({
        category: category as CompanyDocumentCategory,
        page: 1,
        page_size: 100,
      });
      if (response.success && response.data) {
        setDocuments(response.data.documents);
      }
    } catch (err) {
      console.error('Failed to load documents:', err);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleCategoryClick = async (category: string) => {
    // View documents for the selected category
    setSelectedCategoryForView(category);
    setViewMode('documents');
    await fetchDocumentsForCategory(category);
  };

  const handleBackToOverview = () => {
    setViewMode('overview');
    setSelectedCategoryForView(null);
    setDocuments([]);
  };

  const handleAddDocument = () => {
    setSelectedCategory(undefined);
    setIsUploadModalOpen(true);
  };

  const handleQuickUpload = (category: CompanyDocumentCategory) => {
    setSelectedCategory(category);
    setIsUploadModalOpen(true);
  };

  const handleModalClose = () => {
    setIsUploadModalOpen(false);
    setSelectedCategory(undefined);
  };

  const handleUploadSuccess = () => {
    // Refresh data after successful upload
    fetchData();
  };

  const handleSetupWizard = () => {
    navigate('/knowledge-base/setup');
  };

  const handleAddDocumentToCategory = () => {
    setSelectedCategory(selectedCategoryForView || undefined);
    setIsUploadModalOpen(true);
  };

  const handleViewDocument = (doc: CompanyDocument) => {
    if (doc.file_url) {
      window.open(doc.file_url, '_blank');
    }
  };

  const handleDownloadDocument = (doc: CompanyDocument) => {
    if (doc.file_url) {
      window.open(doc.file_url, '_blank');
    }
  };

  const handleDeleteDocument = async (docId: string) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }
    try {
      await KnowledgeBaseApi.deleteDocument(docId);
      // Refresh the documents list
      if (selectedCategoryForView) {
        await fetchDocumentsForCategory(selectedCategoryForView);
      }
      // Refresh stats
      await fetchData();
    } catch (err) {
      console.error('Failed to delete document:', err);
      alert('Failed to delete document. Please try again.');
    }
  };

  const renderDocumentsView = () => {
    const selectedCategoryInfo = categories.find(c => c.category === selectedCategoryForView);
    const categoryDisplayName = selectedCategoryInfo?.display_name || selectedCategoryForView;

    return (
      <DocumentsViewContainer>
        <DocumentsHeader>
          <DocsBreadcrumb aria-label="Breadcrumb">
            <DocsBackLink type="button" onClick={handleBackToOverview}>
              <ChevronLeft size={14} />
              Knowledge base
            </DocsBackLink>
            <DocsHeading>
              <DocsHeadingPath>
                <FolderOpen size={18} />
                <ChevronRight size={14} />
              </DocsHeadingPath>
              {categoryDisplayName}
            </DocsHeading>
          </DocsBreadcrumb>
          <PrimaryButton onClick={handleAddDocumentToCategory}>
            <Plus size={16} />
            Add document
          </PrimaryButton>
        </DocumentsHeader>

        {loadingDocuments ? (
          <LoadingShell>
            <LoadingSpinnerEl />
            Loading documents…
          </LoadingShell>
        ) : documents.length === 0 ? (
          <EmptyState>
            <EmptyIcon>
              <FolderOpen size={32} />
            </EmptyIcon>
            <EmptyTitle>No documents in this category yet</EmptyTitle>
            <EmptyDescription>
              Upload a document here to ground the AI's tender drafts in real evidence from your
              organisation.
            </EmptyDescription>
            <PrimaryButton onClick={handleAddDocumentToCategory}>
              <Upload size={16} />
              Upload document
            </PrimaryButton>
          </EmptyState>
        ) : (
          <DocumentsList>
            {documents.map(doc => (
              <DocumentCard key={doc.id}>
                <DocumentCardHeader>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <DocumentTitle>{doc.title}</DocumentTitle>
                    <DocumentMeta>
                      <MetaPill>
                        <span className="icon">
                          <Clock size={11} />
                        </span>
                        Added {new Date(doc.created_at).toLocaleDateString()}
                      </MetaPill>
                      {doc.file_name && (
                        <MetaPill>
                          <span className="icon">
                            <FileText size={11} />
                          </span>
                          {doc.file_name}
                        </MetaPill>
                      )}
                      {doc.status && (
                        <MetaPill>
                          <span className="icon">
                            <CheckCircle2 size={11} />
                          </span>
                          {doc.status}
                        </MetaPill>
                      )}
                    </DocumentMeta>
                  </div>
                  <DocumentActions>
                    {doc.file_url && (
                      <ActionIconButton
                        type="button"
                        onClick={() => handleDownloadDocument(doc)}
                        aria-label="Download document"
                        title="Download"
                      >
                        <Download size={16} />
                      </ActionIconButton>
                    )}
                    <ActionIconButton
                      type="button"
                      onClick={() => handleViewDocument(doc)}
                      aria-label="View document"
                      title="View"
                    >
                      <Eye size={16} />
                    </ActionIconButton>
                    <ActionIconButton
                      type="button"
                      $tone="danger"
                      onClick={() => handleDeleteDocument(doc.id)}
                      aria-label="Delete document"
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </ActionIconButton>
                  </DocumentActions>
                </DocumentCardHeader>

                {doc.summary && <DocumentSummary>{doc.summary}</DocumentSummary>}

                {doc.key_points && doc.key_points.length > 0 && (
                  <DocumentKeyPoints>
                    {doc.key_points.slice(0, 3).map((point, idx) => (
                      <KeyPoint key={idx}>{point}</KeyPoint>
                    ))}
                  </DocumentKeyPoints>
                )}

                {doc.tags && doc.tags.length > 0 && (
                  <TagsContainer>
                    {doc.tags.map((tag, idx) => (
                      <Tag key={idx}>{tag}</Tag>
                    ))}
                  </TagsContainer>
                )}
              </DocumentCard>
            ))}
          </DocumentsList>
        )}
      </DocumentsViewContainer>
    );
  };

  // Categories that contain at least one expiring-soon document
  const expiringCategorySet = useMemo(() => {
    const set = new Set<string>();
    if (stats?.expiring_soon) {
      stats.expiring_soon.forEach(d => set.add(d.category));
    }
    return set;
  }, [stats]);

  // Quick lookup of doc titles per category from stats arrays
  const categoryDocTitles = useMemo(() => {
    const map = new Map<string, string[]>();
    const push = (cat: string, title: string) => {
      const arr = map.get(cat) || [];
      arr.push(title);
      map.set(cat, arr);
    };
    stats?.most_used_documents?.forEach(d => push(d.category, d.title));
    stats?.recently_added?.forEach(d => push(d.category, d.title));
    stats?.expiring_soon?.forEach(d => push(d.category, d.title));
    return map;
  }, [stats]);

  const normalizedQuery = debouncedSearch.trim().toLowerCase();
  const isSearching = normalizedQuery.length > 0;

  // Apply filter chip first, then narrow by search query
  const matchesChipFilter = useCallback(
    (c: CategoryInfo): boolean => {
      switch (activeFilter) {
        case 'critical':
          return isCategoryCritical(c);
        case 'filled':
          return c.document_count > 0;
        case 'empty':
          return c.document_count === 0;
        case 'expiring':
          return expiringCategorySet.has(c.category);
        case 'all':
        default:
          return true;
      }
    },
    [activeFilter, expiringCategorySet]
  );

  const matchesSearch = useCallback(
    (c: CategoryInfo): boolean => {
      if (!isSearching) return true;
      const haystacks: string[] = [
        c.display_name || '',
        c.description || '',
        c.category || '', // category key acts as a synonym proxy
      ];
      const docTitles = categoryDocTitles.get(c.category) || [];
      haystacks.push(...docTitles);
      return haystacks.some(h => h.toLowerCase().includes(normalizedQuery));
    },
    [isSearching, normalizedQuery, categoryDocTitles]
  );

  // Counts for each chip respect the current dataset (not the filtered subset)
  const chipCounts = useMemo(() => {
    const all = categories.length;
    const critical = categories.filter(isCategoryCritical).length;
    const filled = categories.filter(c => c.document_count > 0).length;
    const empty = categories.filter(c => c.document_count === 0).length;
    const expiring = categories.filter(c => expiringCategorySet.has(c.category)).length;
    return { all, critical, filled, empty, expiring };
  }, [categories, expiringCategorySet]);

  // Per-group filtered category list — used for both rendering and jump-list disable state
  const groupVisibility = useMemo(() => {
    const result: Record<string, { infos: CategoryInfo[]; visible: boolean }> = {};
    Object.entries(CATEGORY_GROUPS).forEach(([groupName, groupCategories]) => {
      const groupInfos = categories.filter(c => groupCategories.includes(c.category));
      const filtered = groupInfos.filter(c => matchesChipFilter(c) && matchesSearch(c));
      result[groupName] = { infos: filtered, visible: filtered.length > 0 };
    });
    return result;
  }, [categories, matchesChipFilter, matchesSearch]);

  const totalMatches = useMemo(
    () => Object.values(groupVisibility).reduce((sum, g) => sum + g.infos.length, 0),
    [groupVisibility]
  );

  // While searching, expand all groups; restore prior state when search is cleared
  const effectiveExpanded = useCallback(
    (groupName: string): boolean => {
      if (isSearching) return true;
      return expandedGroups[groupName] === true;
    },
    [isSearching, expandedGroups]
  );

  // Active jump-chip highlighting via IntersectionObserver
  useEffect(() => {
    if (viewMode !== 'overview') return;
    const observers: IntersectionObserver[] = [];
    const visibleMap = new Map<string, number>();

    const obs = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          const groupName = entry.target.getAttribute('data-group-name');
          if (!groupName) return;
          if (entry.isIntersecting) {
            visibleMap.set(groupName, entry.intersectionRatio);
          } else {
            visibleMap.delete(groupName);
          }
        });
        if (visibleMap.size > 0) {
          let topGroup: string | null = null;
          let topRatio = -1;
          visibleMap.forEach((ratio, name) => {
            if (ratio > topRatio) {
              topRatio = ratio;
              topGroup = name;
            }
          });
          if (topGroup) setActiveJumpGroup(topGroup);
        }
      },
      { rootMargin: '-80px 0px -60% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );

    Object.entries(groupRefs.current).forEach(([, el]) => {
      if (el) obs.observe(el);
    });
    observers.push(obs);
    return () => {
      observers.forEach(o => o.disconnect());
    };
  }, [viewMode, groupVisibility]);

  const handleJumpClick = (groupName: string) => {
    const el = groupRefs.current[groupName];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveJumpGroup(groupName);
    }
  };

  // Resolve the adaptive header status pill tone & copy.
  const headerStatus = useMemo<{ tone: StatusTone; label: React.ReactNode } | null>(() => {
    if (!setupProgress) return null;
    const pct = setupProgress.completion_percentage;
    const missing = setupProgress.missing_critical_categories.length;
    if (pct >= 100) {
      return { tone: 'success', label: <strong>Knowledge base complete</strong> };
    }
    if (missing > 0) {
      return {
        tone: 'warning',
        label: (
          <>
            <strong>{missing}</strong> critical missing
          </>
        ),
      };
    }
    return {
      tone: 'primary',
      label: (
        <>
          <strong>{pct.toFixed(0)}%</strong> complete
        </>
      ),
    };
  }, [setupProgress]);

  // For the "Most Used Doc" stat — compute a percentage relative to total docs
  // for the optional micro-bar. Keeps the visual subtle even on a tiny dataset.
  // Must be declared before any early return — hooks order is contractual.
  const mostUsedPct = useMemo(() => {
    if (!stats || !stats.most_used_documents.length || !stats.total_documents) return 0;
    const top = stats.most_used_documents[0].usage_count;
    return Math.min(100, (top / Math.max(stats.total_documents, 1)) * 100);
  }, [stats]);

  // Loading state — match ProfilePage's centered spinner pattern.
  if (loading) {
    return (
      <KbContainer>
        <Particles particleCount={30} />
        <Container>
          <ContentWrapper>
            <LoadingShell>
              <LoadingSpinnerEl />
              Loading knowledge base…
            </LoadingShell>
          </ContentWrapper>
        </Container>
      </KbContainer>
    );
  }

  // Filter-chip metadata. The 'critical' chip uses error tone, 'expiring' uses
  // warning tone, the rest use primary — surfaces semantic urgency at a glance.
  const filterChips: Array<{ id: FilterChipId; label: string; count: number; tone: ChipTone }> = [
    { id: 'all', label: 'All', count: chipCounts.all, tone: 'primary' },
    { id: 'critical', label: 'Critical', count: chipCounts.critical, tone: 'error' },
    { id: 'filled', label: 'Filled', count: chipCounts.filled, tone: 'primary' },
    { id: 'empty', label: 'Empty', count: chipCounts.empty, tone: 'primary' },
    { id: 'expiring', label: 'Expiring soon', count: chipCounts.expiring, tone: 'warning' },
  ];

  return (
    <KbContainer>
      <Particles particleCount={30} />
      <Container>
        <ContentWrapper>
          {/* ======================= PAGE HEADER ======================= */}
          <PageHeader>
            <HeaderLeft>
              <Crumb>Knowledge base</Crumb>
              <Title>Company knowledge base</Title>
              <Subtitle>
                Documents your AI uses to draft tender responses. Group, search, and curate the
                evidence that powers every bid.
              </Subtitle>
            </HeaderLeft>
            <HeaderRight>
              {headerStatus && (
                <StatusPill $tone={headerStatus.tone}>
                  <span className="pulse" />
                  <span>{headerStatus.label}</span>
                </StatusPill>
              )}
              <ActionsRow>
                <SecondaryButton type="button" onClick={handleSetupWizard}>
                  <Settings size={16} />
                  Setup wizard
                </SecondaryButton>
                <PrimaryButton type="button" onClick={handleAddDocument}>
                  <Plus size={16} />
                  Add document
                </PrimaryButton>
              </ActionsRow>
            </HeaderRight>
          </PageHeader>

          {/* ======================= CONDITIONAL VIEW ======================= */}
          {viewMode === 'documents' ? (
            renderDocumentsView()
          ) : (
            <>
              {/* Setup Progress */}
              {setupProgress && (
                <ProgressPanel>
                  <ProgressTopRow>
                    <div style={{ minWidth: 0 }}>
                      <Eyebrow>
                        <span className="num">01</span>
                        <span>·</span>
                        <span>Status</span>
                      </Eyebrow>
                      <SubHeading>Setup progress</SubHeading>
                      <SubHelper>
                        How much of your knowledge base is in place. Hitting the
                        <em>recommended</em> mark unlocks reliable AI drafts.
                      </SubHelper>
                    </div>
                    <ProgressRight>
                      <BigPercent $pct={setupProgress.completion_percentage}>
                        {setupProgress.completion_percentage.toFixed(0)}%
                      </BigPercent>
                      <InlineChips>
                        <InlineChip $tone="success">
                          <CheckCircle2 size={12} />
                          <span>
                            <strong>{setupProgress.completed_categories}</strong>
                            {' / '}
                            {setupProgress.total_categories} categories
                          </span>
                        </InlineChip>
                        {setupProgress.missing_critical_categories.length > 0 && (
                          <InlineChip $tone="warning">
                            <AlertCircle size={12} />
                            <span>
                              <strong>{setupProgress.missing_critical_categories.length}</strong>{' '}
                              critical missing
                            </span>
                          </InlineChip>
                        )}
                      </InlineChips>
                    </ProgressRight>
                  </ProgressTopRow>
                  <ProgressTrack>
                    <ProgressFill $pct={setupProgress.completion_percentage} />
                  </ProgressTrack>
                  <TickWrap aria-hidden="true">
                    <Tick />
                    <TickLabel>Recommended</TickLabel>
                  </TickWrap>
                </ProgressPanel>
              )}

              {/* Single actionable warning banner */}
              {setupProgress &&
                (setupProgress.missing_critical_categories.length > 0 ||
                  setupProgress.completion_percentage < 100) && (
                  <WarningBanner>
                    <span className="icon">
                      <AlertCircle size={14} />
                    </span>
                    <span className="copy">
                      <strong>
                        {setupProgress.missing_critical_categories.length} critical docs missing
                      </strong>{' '}
                      — setup is {setupProgress.completion_percentage.toFixed(0)}% complete.
                    </span>
                    <WarningAction type="button" onClick={handleSetupWizard}>
                      Set up now
                      <ChevronRight size={14} />
                    </WarningAction>
                  </WarningBanner>
                )}

              {/* Stats Grid — hidden entirely on a fresh KB */}
              {stats && stats.total_documents > 0 && (
                <StatsGrid>
                  <StatCard>
                    <StatIconTile $tone="primary">
                      <FileText size={16} />
                    </StatIconTile>
                    <StatBody>
                      <StatValue>{stats.total_documents}</StatValue>
                      <StatLabel>Total documents</StatLabel>
                    </StatBody>
                  </StatCard>
                  <StatCard>
                    <StatIconTile $tone="success">
                      <CheckCircle2 size={16} />
                    </StatIconTile>
                    <StatBody>
                      <StatValue>{stats.categories_with_documents.length}</StatValue>
                      <StatLabel>Categories covered</StatLabel>
                    </StatBody>
                  </StatCard>
                  <StatCard>
                    <StatIconTile $tone="warning">
                      <Clock size={16} />
                    </StatIconTile>
                    <StatBody>
                      <StatValue>{stats.expiring_soon.length}</StatValue>
                      <StatLabel>Expiring soon</StatLabel>
                    </StatBody>
                  </StatCard>
                  <StatCard>
                    <StatIconTile $tone="accent">
                      <TrendingUp size={16} />
                    </StatIconTile>
                    <StatBody>
                      <StatValue>
                        {stats.most_used_documents.length > 0
                          ? stats.most_used_documents[0].usage_count
                          : 0}
                      </StatValue>
                      <StatLabel>Most used doc</StatLabel>
                    </StatBody>
                    {mostUsedPct > 0 && <MicroBar $pct={mostUsedPct} aria-hidden="true" />}
                  </StatCard>
                </StatsGrid>
              )}

              {/* First-run focused onboarding when KB is empty */}
              {!error && stats && stats.total_documents === 0 && !forceFullView ? (
                <FirstRunHero>
                  <FirstRunEyebrow>
                    <span className="dot" />
                    Getting started
                  </FirstRunEyebrow>
                  <FirstRunTitle>Set up your knowledge base in 3 steps.</FirstRunTitle>
                  <FirstRunSub>
                    Drop in three critical documents and the AI starts drafting tender responses
                    grounded in your real evidence — not generic boilerplate.
                  </FirstRunSub>
                  <FirstRunChipRow>
                    <FirstRunChip
                      type="button"
                      onClick={() => handleQuickUpload('company_overview')}
                    >
                      <span className="icon">
                        <Building2 size={18} />
                      </span>
                      <span className="body">
                        <span className="meta">Step 01</span>
                        <span className="label">Company overview</span>
                      </span>
                    </FirstRunChip>
                    <FirstRunChip type="button" onClick={() => handleQuickUpload('team_cv')}>
                      <span className="icon">
                        <User size={18} />
                      </span>
                      <span className="body">
                        <span className="meta">Step 02</span>
                        <span className="label">Team CV / résumé</span>
                      </span>
                    </FirstRunChip>
                    <FirstRunChip type="button" onClick={() => handleQuickUpload('case_study')}>
                      <span className="icon">
                        <BarChart2 size={18} />
                      </span>
                      <span className="body">
                        <span className="meta">Step 03</span>
                        <span className="label">Case study</span>
                      </span>
                    </FirstRunChip>
                  </FirstRunChipRow>
                  <FirstRunFooter>
                    <FirstRunLink type="button" onClick={handleSetupWizard}>
                      Start setup wizard
                      <ChevronRight size={14} />
                    </FirstRunLink>
                    <FirstRunLink type="button" onClick={() => setForceFullView(true)}>
                      Browse all categories
                      <ChevronRight size={14} />
                    </FirstRunLink>
                  </FirstRunFooter>
                </FirstRunHero>
              ) : (
                <CategoriesSection>
                  <SectionHead>
                    <Eyebrow>
                      <span className="num">02</span>
                      <span>·</span>
                      <span>Categories</span>
                    </Eyebrow>
                    <SubHeading>Browse by category</SubHeading>
                    <SubHelper>
                      The AI looks here when drafting. Each tile shows how many documents are
                      available and which need your attention.
                    </SubHelper>
                  </SectionHead>

                  {error && (
                    <ErrorBand role="alert">
                      <span className="icon">
                        <AlertCircle size={16} />
                      </span>
                      <div className="body">
                        <h4>Failed to load categories</h4>
                        <p>{error}</p>
                        <RetryButton type="button" onClick={fetchData}>
                          <Upload size={14} />
                          Retry
                        </RetryButton>
                      </div>
                    </ErrorBand>
                  )}

                  {!error && categories.length === 0 && (
                    <EmptyState>
                      <EmptyIcon>
                        <AlertCircle size={32} />
                      </EmptyIcon>
                      <EmptyTitle>No categories available</EmptyTitle>
                      <EmptyDescription>
                        Categories couldn't be loaded. Check the backend is running, or try a
                        refresh.
                      </EmptyDescription>
                      <PrimaryButton type="button" onClick={fetchData}>
                        <Upload size={16} />
                        Refresh
                      </PrimaryButton>
                    </EmptyState>
                  )}

                  {!error && categories.length > 0 && (
                    <>
                      <StickyToolbar>
                        <SearchBlock>
                          <SearchInputWrapper>
                            <SearchIconLeft>
                              <Search size={16} />
                            </SearchIconLeft>
                            <SearchInput
                              type="text"
                              value={searchQuery}
                              onChange={e => setSearchQuery(e.target.value)}
                              placeholder="Search documents or categories…"
                              aria-label="Search documents or categories"
                            />
                          </SearchInputWrapper>
                          {isSearching && (
                            <SearchMatchInfo>
                              {totalMatches} {totalMatches === 1 ? 'match' : 'matches'} for{' '}
                              <code>{debouncedSearch.trim()}</code>
                            </SearchMatchInfo>
                          )}
                        </SearchBlock>
                        <FilterChipsRow>
                          {filterChips.map(chip => {
                            const active = activeFilter === chip.id;
                            return (
                              <FilterChip
                                key={chip.id}
                                type="button"
                                $active={active}
                                $tone={chip.tone}
                                onClick={() => setActiveFilter(chip.id)}
                              >
                                {chip.label}
                                <ChipCount $active={active}>{chip.count}</ChipCount>
                              </FilterChip>
                            );
                          })}
                        </FilterChipsRow>
                      </StickyToolbar>

                      <JumpList role="navigation" aria-label="Category groups">
                        {Object.keys(CATEGORY_GROUPS).map(groupName => {
                          const visible = groupVisibility[groupName]?.visible ?? false;
                          const greyedOut = (isSearching || activeFilter !== 'all') && !visible;
                          const active = activeJumpGroup === groupName && !greyedOut;
                          return (
                            <JumpChip
                              key={groupName}
                              type="button"
                              $active={active}
                              $disabled={greyedOut}
                              disabled={greyedOut}
                              onClick={() => !greyedOut && handleJumpClick(groupName)}
                            >
                              {GROUP_SHORT_LABELS[groupName] || groupName}
                            </JumpChip>
                          );
                        })}
                      </JumpList>

                      {totalMatches === 0 && isSearching && (
                        <SearchEmptyRow>
                          No categories or documents match <code>{debouncedSearch.trim()}</code>.
                        </SearchEmptyRow>
                      )}

                      {Object.entries(CATEGORY_GROUPS).map(([groupName, groupCategories]) => {
                        const allGroupInfos = categories.filter(c =>
                          groupCategories.includes(c.category)
                        );
                        if (allGroupInfos.length === 0) return null;

                        const visibleInfos = groupVisibility[groupName]?.infos ?? [];

                        // Hide groups with zero matches when a filter or search is active
                        if (
                          (isSearching || activeFilter !== 'all') &&
                          visibleInfos.length === 0
                        ) {
                          return null;
                        }

                        const filledCount = allGroupInfos.filter(c => c.document_count > 0).length;
                        const totalCount = allGroupInfos.length;
                        const isExpanded = effectiveExpanded(groupName);
                        const allFilled = filledCount === totalCount && totalCount > 0;

                        return (
                          <CategoryGroup
                            key={groupName}
                            ref={el => {
                              groupRefs.current[groupName] = el;
                            }}
                            data-group-name={groupName}
                          >
                            <CategoryGroupHeader
                              type="button"
                              aria-expanded={isExpanded}
                              onClick={() => !isSearching && toggleGroup(groupName)}
                            >
                              <GroupChevron $expanded={isExpanded}>
                                <ChevronDown size={16} />
                              </GroupChevron>
                              <GroupIconTile aria-hidden="true">
                                {CATEGORY_ICONS[groupName] || '📁'}
                              </GroupIconTile>
                              <GroupName>{groupName}</GroupName>
                              <GroupCountPill $allFilled={allFilled}>
                                {filledCount} / {totalCount}
                              </GroupCountPill>
                            </CategoryGroupHeader>
                            {isExpanded && (
                              <CategoryGrid>
                                {visibleInfos.map(category => {
                                  const hasDocuments = category.document_count > 0;
                                  const isCritical =
                                    isCategoryCritical(category) && category.document_count === 0;
                                  return (
                                    <CategoryCard
                                      key={category.category}
                                      $hasDocuments={hasDocuments}
                                      $isCritical={isCritical}
                                      role="button"
                                      tabIndex={0}
                                      aria-label={`${category.display_name}, ${category.document_count} document${category.document_count === 1 ? '' : 's'}`}
                                      onClick={() => handleCategoryClick(category.category)}
                                      onKeyDown={e => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                          e.preventDefault();
                                          handleCategoryClick(category.category);
                                        }
                                      }}
                                    >
                                      <StatusIconTile $filled={hasDocuments}>
                                        {hasDocuments ? (
                                          <CheckCircle2 size={16} />
                                        ) : (
                                          <FolderOpen size={16} />
                                        )}
                                      </StatusIconTile>
                                      <CategoryBody>
                                        <CategoryName>
                                          <CategoryNameText>
                                            {category.display_name}
                                          </CategoryNameText>
                                          {isCritical && <CriticalTag>Critical</CriticalTag>}
                                        </CategoryName>
                                        {category.description && (
                                          <CategoryDescription>
                                            {category.description}
                                          </CategoryDescription>
                                        )}
                                      </CategoryBody>
                                      <CountPill $hasDocuments={hasDocuments}>
                                        {category.document_count}
                                        <span style={{ opacity: 0.7 }}>
                                          {category.document_count === 1 ? ' doc' : ' docs'}
                                        </span>
                                      </CountPill>
                                      <CategoryChevron>
                                        <ChevronRight size={16} className="kb-tile-chevron" />
                                      </CategoryChevron>
                                    </CategoryCard>
                                  );
                                })}
                              </CategoryGrid>
                            )}
                          </CategoryGroup>
                        );
                      })}
                    </>
                  )}
                </CategoriesSection>
              )}
            </>
          )}
        </ContentWrapper>
      </Container>

      {/* Document Upload Modal */}
      <DocumentUploadModal
        isOpen={isUploadModalOpen}
        onClose={handleModalClose}
        initialCategory={selectedCategory as any}
        onSuccess={handleUploadSuccess}
      />
    </KbContainer>
  );
};

export default KnowledgeBasePage;
